import usePrivateAxios from "@/common/api/usePrivateAxios";
import { useQuery } from "@tanstack/react-query";

export type MissionProvider = {
  provider_klass: string;
  display_name?: string;
  description?: string;
};

const normalizeProviders = (payload: unknown): MissionProvider[] => {
  if (!Array.isArray(payload)) return [];

  return payload
    .map((item) => {
      if (typeof item === "string") {
        return { provider_klass: item };
      }

      if (item && typeof item === "object") {
        const provider =
          (item as { provider_klass?: string; providerKlass?: string })
            .provider_klass ??
          (item as { providerKlass?: string }).providerKlass;
        const displayName =
          (item as { display_name?: string; displayName?: string })
            .display_name ?? (item as { displayName?: string }).displayName;
        const description = (item as { description?: string }).description;

        if (typeof provider === "string" && provider.length > 0) {
          return {
            provider_klass: provider,
            display_name:
              typeof displayName === "string" ? displayName : undefined,
            description:
              typeof description === "string" ? description : undefined,
          };
        }
      }

      return null;
    })
    .filter((provider): provider is MissionProvider =>
      Boolean(provider?.provider_klass),
    );
};

type UseMissionProvidersProps<T> = {
  mapper?: (data: MissionProvider[]) => T;
};

export const useMissionProviders = <T = MissionProvider[]>({
  mapper = ((data: MissionProvider[]) => data as unknown as T),
}: UseMissionProvidersProps<T> = {}) => {
  const privateAxios = usePrivateAxios();

  return useQuery({
    queryKey: ["mission-providers"],
    queryFn: () =>
      privateAxios.get("/tmais/mission_requirements/available_providers"),
    select: (response: { data?: any }) => {
      const candidates = [
        response.data?.data,
        response.data?.available_providers,
        response.data?.data?.available_providers,
        response.data,
      ];
      const payload =
        candidates.find((candidate) => Array.isArray(candidate)) ?? [];
      return mapper(normalizeProviders(payload));
    },
  });
};
